import React from 'react';
import { ArrowRight, Leaf, Globe, TrendingUp } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

// Sayaçlar için başlangıç değerleri ve artış oranları
const START_DATE = new Date('2023-01-01');
const INITIAL_SITES = 1000;
const SITES_PER_DAY = 2; // Her gün 2 yeni nötr site
const INITIAL_CO2 = 50000; // ton
const CO2_PER_DAY = 120; // Her gün 120 ton tasarruf

function getDaysSinceStart() {
  const now = new Date();
  const diff = now - START_DATE;
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

function getDynamicStats() {
  const days = getDaysSinceStart();
  const sites = INITIAL_SITES + days * SITES_PER_DAY;
  const co2 = INITIAL_CO2 + days * CO2_PER_DAY;
  return {
    sites,
    co2
  };
}

// 10 farklı başlık ve alt başlık çifti - artık dil context'inden gelecek
const getHeroTexts = (t) => [
  {
    title: t('hero-title'),
    subtitle: t('hero-subtitle'),
  },
  {
    title: t('hero-title-2'),
    subtitle: t('hero-subtitle-2'),
  },
  {
    title: t('hero-title-3'),
    subtitle: t('hero-subtitle-3'),
  },
  {
    title: t('hero-title-4'),
    subtitle: t('hero-subtitle-4'),
  },
  {
    title: t('hero-title-5'),
    subtitle: t('hero-subtitle-5'),
  },
  {
    title: t('hero-title-6'),
    subtitle: t('hero-subtitle-6'),
  },
  {
    title: t('hero-title-7'),
    subtitle: t('hero-subtitle-7'),
  },
  {
    title: t('hero-title-8'),
    subtitle: t('hero-subtitle-8'),
  },
  {
    title: t('hero-title-9'),
    subtitle: t('hero-subtitle-9'),
  },
  {
    title: t('hero-title-10'),
    subtitle: t('hero-subtitle-10'),
  },
];

const Hero = () => {
  const { t } = useLanguage();
  const stats = getDynamicStats();
  
  // Sayfa her yüklendiğinde rastgele bir başlık-alt başlık çifti seç
  const heroTexts = getHeroTexts(t);
  const randomIndex = Math.floor(Math.random() * heroTexts.length);
  const selectedText = heroTexts[randomIndex];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img 
          src="https://res.cloudinary.com/debiu7z1b/image/upload/v1726695751/treeBackground_gynr4c.webp" 
          alt="Ağaç arkaplan görseli" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/30"></div>
      </div>

      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-20 h-20 bg-primary-200 rounded-full opacity-20 animate-pulse"></div>
        <div className="absolute top-40 right-20 w-16 h-16 bg-primary-300 rounded-full opacity-30 animate-pulse delay-1000"></div>
        <div className="absolute bottom-20 left-1/4 w-12 h-12 bg-primary-400 rounded-full opacity-25 animate-pulse delay-2000"></div>
        <div className="absolute bottom-40 right-1/3 w-24 h-24 bg-primary-100 rounded-full opacity-15 animate-pulse delay-1500"></div>
      </div>

      <div className="container-custom relative z-10 px-4">
        <div className="text-center max-w-4xl mx-auto">
          {/* Main Headline */}
          <h1 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight animate-fade-in break-words">
            {selectedText.title.split(' ').map((word, i) =>
              word.toLowerCase() === 'geleceğe' || word.toLowerCase() === 'future' || word.toLowerCase() === 'zukunft' ? (
                <span key={i} className="text-white bg-primary-500/80 px-1 sm:px-2 rounded-lg inline-block mb-1 sm:mb-0">{word}</span>
              ) : (
                <React.Fragment key={i}>{word} </React.Fragment>
              )
            )}
          </h1>

          {/* Subtitle */}
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white/90 mb-6 sm:mb-8 leading-relaxed animate-slide-up px-2 sm:px-0">
            {selectedText.subtitle}
          </p>

          {/* Call-to-Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center mb-8 sm:mb-12 px-4 sm:px-0">
            <button
              onClick={() => scrollToSection('certificate-form')}
              className="btn-primary flex items-center space-x-2 text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 w-full sm:w-auto"
            >
              <span>{t('hero-cta-primary')}</span>
              <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
            <button
              onClick={() => scrollToSection('verification')}
              className="btn-secondary flex items-center space-x-2 text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 w-full sm:w-auto"
            >
              <span>{t('hero-cta-secondary')}</span>
              <Leaf className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mt-12 sm:mt-16 px-2 sm:px-0">
            <div className="text-center bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6">
              <div className="flex justify-center mb-2 sm:mb-3">
                <Globe className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">15+</div>
              <div className="text-white/80 text-sm sm:text-base">{t('hero-stats-experience')}</div>
            </div>
            <div className="text-center bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6">
              <div className="flex justify-center mb-2 sm:mb-3">
                <Leaf className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">{stats.sites.toLocaleString('tr-TR')}+</div>
              <div className="text-white/80 text-sm sm:text-base">{t('hero-stats-sites')}</div>
            </div>
            <div className="text-center bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6">
              <div className="flex justify-center mb-2 sm:mb-3">
                <TrendingUp className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">{stats.co2.toLocaleString('tr-TR')}+</div>
              <div className="text-white/80 text-sm sm:text-base">{t('hero-stats-savings')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-5 h-8 sm:w-6 sm:h-10 border-2 border-white rounded-full flex justify-center">
          <div className="w-1 h-2 sm:h-3 bg-white rounded-full mt-1 sm:mt-2 animate-pulse"></div>
        </div>
      </div>
    </section>
  );
};

export default Hero; 